// apps/dashboard/src/hooks/useWhiteLabelSiteSocket.js
import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWebsocket } from "@finitless/shared/contexts";
import { useApiStore, userInfo } from "@finitless/shared/stores";
import { DASHBOARD_SITE_QUERY_KEY } from "./useWhiteLabel";

/**
 * Keeps the cached dashboard-finitless Site in sync with backend `sites_update`
 * websocket events (the Cloudflare SSL/domain flow pushes status changes; the UI
 * never polls or triggers SSL actions). Mirrors the internal OrderingPlus
 * business white-label socket subscription. Backend packet: `{ id, ...changes }`.
 */
export const useWhiteLabelSiteSocket = (site) => {
  const queryClient = useQueryClient();
  const { socket, isConnected } = useWebsocket();
  const project = useApiStore((state) => state.project);
  const user = userInfo();
  const siteRef = useRef(site);

  useEffect(() => {
    siteRef.current = site;
  }, [site]);

  const handleSitesUpdate = useCallback(
    (payload) => {
      const current = siteRef.current;
      const updateId = payload?.id ?? payload?.site_id;
      if (!current?.id || !updateId || updateId !== current.id) return;

      const changes = { ...payload };
      delete changes.id;
      delete changes.site_id;

      queryClient.setQueryData(DASHBOARD_SITE_QUERY_KEY, (old) =>
        old ? { ...old, ...changes } : old,
      );
    },
    [queryClient],
  );

  useEffect(() => {
    if (!socket || !isConnected || !site?.id) return;

    const room = { room: "sites", project, role: "manager", user_id: user?.id };
    socket.join(room);
    socket.on("sites_update", handleSitesUpdate);

    return () => {
      socket.off("sites_update", handleSitesUpdate);
      socket.leave(room);
    };
  }, [socket, isConnected, site?.id, project, user?.id, handleSitesUpdate]);

  return { isConnected };
};

export default useWhiteLabelSiteSocket;
